import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Icon from '../../../../ui/Icon'
import { ListRow } from '../../../../listItem/listRow/ListRow'
import { ListCard } from '../../../../listItem/listCard/ListCard'
import { ListingDetails } from '../../../../listing/ListingDetails'
import { RefundStatusBadge } from '../../../payments/paymentBadge/PaymentBadge'

import { CONFIG } from '../../../../../config'
import { CheckoutStatus } from '../../../../../types/booking/booking'
import { DisplayType, ListCardStripe } from '../../../../../types/listing'
import { MyPayment } from '../../../../../types/refundPoint/refundPoint'
import { checkoutPath, formatMoney, formatPoints } from '../../../../../utils/booking/booking'
import { displayDate, displayLocalDateTimeHourMinute } from '../../../../../utils/dateUtils'

const STATUS: Record<CheckoutStatus, { badge: string; stripe: ListCardStripe }> = {
  [CheckoutStatus.CONFIRMED]: { badge: 'badge-success', stripe: 'success' },
  [CheckoutStatus.PENDING_PAYMENT]: { badge: 'badge-warning', stripe: 'warning' },
  [CheckoutStatus.RECONCILING]: { badge: 'badge-warning', stripe: 'warning' },
  [CheckoutStatus.REFUND_PENDING]: { badge: 'badge-info', stripe: 'info' },
  [CheckoutStatus.REFUNDED]: { badge: 'badge-info', stripe: 'neutral' },
  [CheckoutStatus.EXPIRED]: { badge: '', stripe: 'neutral' },
  [CheckoutStatus.CANCELED]: { badge: '', stripe: 'neutral' },
  [CheckoutStatus.PAYMENT_FAILED]: { badge: 'badge-danger', stripe: 'danger' },
}

const ENDED = [CheckoutStatus.EXPIRED, CheckoutStatus.CANCELED, CheckoutStatus.PAYMENT_FAILED]

/** The customer's purchases, newest first: money paid by card or offline and refund points spent. */
export const MyPaymentList = () => {
  const { t, i18n } = useTranslation(['account', 'bookings'])
  const navigate = useNavigate()

  const renderPaid = (item: MyPayment) => {
    const parts = [
      ...(item.amount.amount > 0 ? [formatMoney(item.amount)] : []),
      ...(item.pointsAmount > 0
        ? [t('account.billing.payments.points', { points: formatPoints(item.pointsAmount) })]
        : []),
    ]
    return <span className="mono text-nowrap fw-semibold">{parts.join(' + ')}</span>
  }

  const renderMethod = (item: MyPayment) => {
    const methods = [
      ...(item.paymentMethod && item.amount.amount > 0
        ? [t(`bookings:booking.all-booking-list.payment-method.${item.paymentMethod.toLowerCase()}`)]
        : []),
      ...(item.pointsAmount > 0 ? [t('account.billing.payments.method-points')] : []),
    ]
    return methods.join(' + ') || '—'
  }

  const renderStatus = (item: MyPayment) => (
    <span className="ph-row gap-1 justify-content-center">
      <span className={`badge ${STATUS[item.status].badge}`}>
        {t(`account.billing.payments.status.${item.status.toLowerCase()}`)}
      </span>
      {item.refundStatus && <RefundStatusBadge status={item.refundStatus} />}
    </span>
  )

  const describe = (item: MyPayment) =>
    item.description ?? t(`account.billing.payments.type.${item.type.toLowerCase()}`)

  const renderPayment = (item: MyPayment, viewMode: DisplayType) => {
    const when = `${displayDate(item.createdAt, i18n.language)} ${displayLocalDateTimeHourMinute(item.createdAt)}`
    const open = () => navigate(checkoutPath(item.checkoutId))

    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={item.checkoutId}
          onClick={open}
          cells={[
            { key: 'date', className: 'mono text-center', content: when },
            { key: 'description', className: 'fw-semibold text-center', content: describe(item) },
            {
              key: 'type',
              className: 'text-center',
              content: t(`account.billing.payments.type.${item.type.toLowerCase()}`),
            },
            { key: 'paid', className: 'text-center', content: renderPaid(item) },
            { key: 'method', className: 'text-center', content: renderMethod(item) },
            { key: 'status', className: 'text-center', content: renderStatus(item) },
          ]}
        />
      )
    }

    return (
      <ListCard
        key={item.checkoutId}
        stripe={STATUS[item.status].stripe}
        title={describe(item)}
        subtitle={
          <>
            <Icon name="calendar" size={12} />
            <span>{when}</span>
          </>
        }
        status={renderStatus(item)}
        muted={ENDED.includes(item.status)}
        onOpen={open}
        openLabel={t('account.billing.payments.open')}
        fields={[
          { key: 'paid', label: t('account.billing.payments.header.paid'), value: renderPaid(item) },
          {
            key: 'method',
            label: t('account.billing.payments.header.method'),
            value: renderMethod(item),
            mono: false,
          },
          {
            key: 'type',
            label: t('account.billing.payments.header.type'),
            value: t(`account.billing.payments.type.${item.type.toLowerCase()}`),
            mono: false,
          },
        ]}
      />
    )
  }

  return (
    <ListingDetails<MyPayment>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/payments/mine`}
      initialViewMode={DisplayType.LIST}
      tableColumns={[
        { key: 'date', label: t('account.billing.payments.header.date'), align: 'center' },
        { key: 'description', label: t('account.billing.payments.header.description'), align: 'center' },
        { key: 'type', label: t('account.billing.payments.header.type'), align: 'center' },
        { key: 'paid', label: t('account.billing.payments.header.paid'), align: 'center' },
        { key: 'method', label: t('account.billing.payments.header.method'), align: 'center' },
        { key: 'status', label: t('account.billing.payments.header.status'), align: 'center' },
      ]}
      renderItem={renderPayment}
    />
  )
}
